import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type {
  LueftungGeraetemodell, LueftungGeraetezeile, LueftungPruefantwort, LueftungSeitenzeile,
} from '../../api';
import type { Rahmen } from '../Projektrahmen';
import { PageHeader } from '../../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, FilterBar, KpiCard, KpiReihe, LoadingState,
  StatusBadge, Vorbehalte, formatiereZahl, useFilterInUrl, type FilterDefinition, type Spalte,
} from '../../muster';
import {
  AnnahmenAbschnitt, Elementverweis, NormAbschnitt, Rechenstand, j9Status, optionenAus, schwereStatus,
  seitenVorbehalte, standtext, zahlNachEinheit,
} from './pruefung';

type Antwort = LueftungPruefantwort<LueftungGeraetemodell, LueftungGeraetezeile>;
type Zeile = LueftungSeitenzeile<LueftungGeraetezeile>;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * RLT-GERÄTEPRÜFUNG (TV5, Lauf lueftung-pruefen = J9)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Je RLT-Gerät und Regel genau eine Zeile - auch „erfüllt“: Eine Liste, die nur
 * die Mängel kennt, kann ein nicht geprüftes Gerät nicht von einem einwandfreien
 * unterscheiden. Die Regeln (ErP 1253/2014, DIN EN 13053, DIN EN 16798-3, GEG,
 * ASR A3.6) stehen mit Norm und Fundstelle im Abschnitt „Annahmen und Vorgaben“;
 * keine ist bestätigt, also bleibt der Bericht Vorabzug (S3.5).
 *
 * Bis TV5 standen diese Ergebnisse nur in der Datenbank (Befund TV §5 Nr. 3).
 */
export function LueftungGeraetepruefung() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const [daten, setDaten] = useState<Antwort | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laedt, setLaedt] = useState(true);

  const laden = useCallback(() => {
    setLaedt(true);
    setFehler(null);
    api.lueftungGeraetepruefung(projekt.id)
      .then(setDaten)
      .catch(setFehler)
      .finally(() => setLaedt(false));
  }, [api, projekt.id]);

  useEffect(() => { laden(); }, [laden]);

  const zeilen = useMemo(() => daten?.zeilen ?? [], [daten]);

  const filter = useMemo<FilterDefinition[]>(() => [
    {
      schluessel: 'ergebnis', beschriftung: 'Ergebnis', art: 'mehrfachauswahl',
      optionen: (['nicht_erfuellt', 'nicht_pruefbar', 'entfaellt', 'erfuellt'] as const)
        .map((s) => ({ wert: s, beschriftung: standtext(s) })),
    },
    {
      schluessel: 'anlage', beschriftung: 'Anlage', art: 'auswahl', platzhalter: 'alle',
      optionen: optionenAus(zeilen.map((z) => z.zeile.anlage)),
    },
    {
      schluessel: 'regel', beschriftung: 'Regel', art: 'auswahl', platzhalter: 'alle',
      optionen: optionenAus(zeilen.map((z) => z.zeile.regel)),
    },
    {
      schluessel: 'gruppe', beschriftung: 'Normgruppe', art: 'auswahl', platzhalter: 'alle',
      optionen: optionenAus(zeilen.map((z) => z.zeile.gruppe)),
    },
  ], [zeilen]);
  const [werte, setWerte] = useFilterInUrl(filter);

  const gefiltert = useMemo(() => {
    const ergebnis = (werte.ergebnis as string[]) ?? [];
    return zeilen.filter((z) => (ergebnis.length === 0 || ergebnis.includes(z.zeile.stand))
      && (!werte.anlage || z.zeile.anlage === werte.anlage)
      && (!werte.regel || z.zeile.regel === werte.regel)
      && (!werte.gruppe || z.zeile.gruppe === werte.gruppe));
  }, [zeilen, werte]);

  const spalten = useMemo<Spalte<Zeile>[]>(() => [
    {
      schluessel: 'anlage', titel: 'Anlage', immerSichtbar: true, wert: (z) => z.zeile.anlage,
    },
    { schluessel: 'typ', titel: 'Gerätetyp', wert: (z) => z.zeile.geraetetyp },
    { schluessel: 'regel', titel: 'Regel', wert: (z) => z.zeile.regel },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (z) => z.zeile.bezeichnung },
    {
      schluessel: 'ergebnis', titel: 'Ergebnis', art: 'status', wert: (z) => standtext(z.zeile.stand),
      inhalt: (z) => <StatusBadge {...j9Status(z.zeile.stand, z.zeile.text)} klein />,
    },
    {
      schluessel: 'schwere', titel: 'Schwere', art: 'status', wert: (z) => z.zeile.schwere,
      inhalt: (z) => (z.zeile.stand === 'nicht_erfuellt'
        ? <StatusBadge {...schwereStatus(z.zeile.schwere)} klein />
        : <span className="m-leise">—</span>),
    },
    {
      schluessel: 'ist', titel: 'Ist', art: 'zahl', wert: (z) => z.zeile.istwert,
      inhalt: (z) => z.zeile.istText ?? zahlNachEinheit(z.zeile.istwert, z.zeile.einheit),
    },
    {
      schluessel: 'grenze', titel: 'Grenze', art: 'zahl', wert: (z) => z.zeile.grenzwert,
      inhalt: (z) => z.zeile.grenzText ?? zahlNachEinheit(z.zeile.grenzwert, z.zeile.einheit),
    },
    { schluessel: 'einheit', titel: 'Einheit', wert: (z) => z.zeile.einheit ?? '' },
    {
      schluessel: 'norm', titel: 'Normbezug',
      wert: (z) => [z.zeile.norm, z.zeile.fundstelle].filter(Boolean).join(' · '),
    },
    { schluessel: 'meldung', titel: 'Meldung', wert: (z) => z.zeile.text },
    { schluessel: 'datenquelle', titel: 'Datenquelle', wert: (z) => z.zeile.datenquelle ?? '', versteckt: true },
    { schluessel: 'datenblatt', titel: 'Datenblatt vom', wert: (z) => z.zeile.datenblattAm ?? '', versteckt: true },
    {
      schluessel: 'verweis', titel: 'Element', sortierbar: false,
      inhalt: (z) => <Elementverweis projektId={projekt.id} ziel={z.ziel} ohne="ohne Anlagenseite" />,
    },
  ], [projekt.id]);

  const m = daten?.modell ?? null;
  const ohneLauf = !!daten && !daten.lauf;
  const gesperrt = !daten?.lauf ? { grund: 'Noch kein Lauf lueftung-pruefen.' } : undefined;

  return (
    <>
      <PageHeader
        titel="RLT-Geräteprüfung"
        beschreibung="Je RLT-Gerät und Regel das Ergebnis der Prüfung gegen ErP, DIN EN 13053, DIN EN 16798-3, GEG und ASR A3.6."
        meta={<Rechenstand lauf={daten?.lauf} datenhash={daten?.datenhash} />}
        aktionen={(
          <ExportLeiste ziele={[
            { art: 'pdf', beschriftung: 'PDF LUE-RLT', gesperrt, laden: () => api.lueftungPruefberichtPdf(projekt.id, 'geraete') },
            { art: 'excel', beschriftung: 'Excel', gesperrt, laden: () => api.lueftungPruefListen(projekt.id, 'pruefung', 'geraete') },
          ]}
          />
        )}
      />

      {laedt && !daten && <LoadingState text="RLT-Geräteprüfung wird abgerufen …" zeilen={8} />}
      {fehler !== null && (
        <ErrorState
          meldung="Die RLT-Geräteprüfung konnte nicht abgerufen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      )}

      {ohneLauf && (
        <EmptyState
          titel="Noch keine RLT-Geräteprüfung gerechnet."
          erklaerung="Die Prüfung entsteht mit dem Serverwerkzeug lueftung-pruefen aus den Gerätedatenblättern und der Gebäudeautomation. Sobald es gelaufen ist, steht hier je Gerät und Regel das Ergebnis."
        />
      )}

      {daten?.lauf && m && (
        <>
          <Vorbehalte eintraege={seitenVorbehalte([m])} />

          <KpiReihe>
            <KpiCard titel="RLT-Geräte" wert={m.anlagen} vergleich={`je ${formatiereZahl(m.regeln, 0)} Regeln`} />
            <KpiCard titel="erfüllt" wert={m.zaehlung.erfuellt} vergleich={`von ${formatiereZahl(m.zaehlung.gesamt, 0)}`} />
            <KpiCard
              titel="nicht erfüllt" wert={m.zaehlung.nichtErfuellt}
              zustand={m.zaehlung.nichtErfuellt > 0 ? 'fehler' : 'gut'}
              zustandText={m.zaehlung.nichtErfuellt > 0 ? 'nicht erfüllt' : 'keine'}
            />
            <KpiCard
              titel="nicht prüfbar" wert={m.zaehlung.nichtPruefbar} vergleich="Eingabe fehlt"
              zustand={m.zaehlung.nichtPruefbar > 0 ? 'warnung' : 'gut'}
              zustandText={m.zaehlung.nichtPruefbar > 0 ? 'Datenlücke' : 'keine'}
            />
            <KpiCard titel="entfällt" wert={m.zaehlung.entfaellt} vergleich="Anforderung trifft nicht zu" />
          </KpiReihe>

          <ContentSection
            titel="Ergebnis je Gerät und Regel"
            beschreibung="„Nicht prüfbar“ heißt: eine Eingabe fehlt. „Entfällt“ heißt: Die Anforderung trifft auf dieses Gerät nicht zu; der Grund steht in der Meldung. Gerätetyp ZLA: Zu-/Abluftgerät (ErP „BVU“), ELA: Einrichtungsgerät („UVU“)."
            werkzeuge={<FilterBar filter={filter} werte={werte} beiAenderung={setWerte} inLeiste={4} />}
          >
            <DataTable<Zeile>
              name="RLT-Geräteprüfung"
              spalten={spalten}
              zeilen={gefiltert}
              schluessel={(z) => `${z.zeile.anlage}|${z.zeile.regel}|${z.zeile.variante}|${z.ziel.plantId ?? ''}`}
              ausfuhr
              ausfuhrDateiname="rlt-geraetepruefung.csv"
              suchePlatzhalter="Anlage, Regel, Meldung …"
              leer={(
                <EmptyState
                  klein
                  titel="Keine Zeile passt zu den Filtern."
                  erklaerung="Die Prüfung hat Zeilen; die gesetzten Filter schließen alle aus. Filter zurücksetzen zeigt wieder alle."
                />
              )}
            />
          </ContentSection>

          <AnnahmenAbschnitt annahmen={m.annahmen} name="RLT-Geräteprüfung" />
          <NormAbschnitt normen={m.normen} name="RLT-Geräteprüfung" />
        </>
      )}
    </>
  );
}
